import { HttpClient } from "@angular/common/http"
import { NgClass } from "@angular/common"
import { Component, OnDestroy, OnInit } from "@angular/core"
import { Subscription } from "rxjs"
import { Router, RouterOutlet, RouterLink, RouterLinkActive } from "@angular/router"
import { MatTooltip, MatTooltipModule } from "@angular/material/tooltip"
import { MatIcon } from "@angular/material/icon"
import { MsalService } from "@azure/msal-angular"
import { environment } from "src/environments/environment"
import { Application, ApplicationState, NavItem } from "../../models"
import { ContextService } from "../../services"

@Component({
    selector: "valben-navigation",
    templateUrl: "./navigation.page.html",
    styles: [`
        .app-topbar { background-color: light-dark(#214392, var(--vb-chrome)); }

        .app-topbar-icon {
            display: inline-flex;
            height: 2.125rem;
            width: 2.125rem;
            flex-shrink: 0;
            align-items: center;
            justify-content: center;
            border: 0;
            border-radius: 9999px;
            background-color: rgb(255 255 255 / 0.1);
            color: rgb(255 255 255 / 0.9);
            cursor: pointer;
            transition: background-color 150ms ease, color 150ms ease;
        }

        .app-topbar-icon:hover {
            background-color: rgb(255 255 255 / 0.2);
            color: #fff;
        }

        .app-topbar-icon:focus-visible {
            outline: 2px solid #fff;
            outline-offset: 2px;
        }

        .app-theme-toggle svg {
            height: 1.375rem;
            width: 1.375rem;
        }

        .app-theme-orb {
            fill: currentColor;
            transform-origin: 12px 12px;
            transition: transform 420ms cubic-bezier(0.4, 0, 0.2, 1);
        }

        .app-theme-bite {
            transition: transform 420ms cubic-bezier(0.4, 0, 0.2, 1);
        }

        .app-theme-rays {
            transform-origin: 12px 12px;
            transition: transform 420ms cubic-bezier(0.4, 0, 0.2, 1), opacity 220ms ease;
        }

        .app-theme-toggle.is-moon .app-theme-bite {
            transform: translate(-7.5px, 2px);
        }

        .app-theme-toggle.is-moon .app-theme-orb {
            transform: scale(1.18) rotate(-18deg);
        }

        .app-theme-toggle.is-moon .app-theme-rays {
            transform: rotate(45deg) scale(0.35);
            opacity: 0;
        }

        .app-theme-toggle:hover .app-theme-rays {
            transform: rotate(22deg);
        }

        .app-theme-toggle.is-moon:hover .app-theme-rays {
            transform: rotate(45deg) scale(0.35);
        }

        .app-theme-toggle.is-moon:hover .app-theme-orb {
            transform: scale(1.22) rotate(-6deg);
        }

        .app-theme-toggle:active {
            transform: scale(0.9);
        }

        @media (prefers-reduced-motion: reduce) {
            .app-theme-orb,
            .app-theme-bite,
            .app-theme-rays {
                transition-duration: 1ms;
            }

            .app-theme-toggle:active {
                transform: none;
            }
        }

        .app-topbar-divider {
            width: 1px;
            height: 1.375rem;
            flex-shrink: 0;
            background-color: rgb(255 255 255 / 0.22);
        }

        .app-topbar-user {
            color: rgb(255 255 255 / 0.92);
            font-size: 0.875rem;
            white-space: nowrap;
        }

        .app-topbar-logout {
            position: relative;
            display: inline-flex;
            align-items: center;
            gap: 0.4375rem;
            height: 2.125rem;
            flex-shrink: 0;
            padding: 0 0.9375rem;
            overflow: hidden;
            border: 1px solid rgb(255 255 255 / 0.4);
            border-radius: 9999px;
            background-color: transparent;
            color: #fff;
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            transition: border-color 200ms ease, color 200ms ease, transform 120ms ease;
        }

        .app-topbar-logout::before {
            content: "";
            position: absolute;
            inset: 0;
            background-color: #fff;
            transform: translateX(-101%);
            transition: transform 260ms cubic-bezier(0.4, 0, 0.2, 1);
        }

        .app-topbar-logout > * {
            position: relative;
        }

        .app-topbar-logout:hover,
        .app-topbar-logout:focus-visible {
            border-color: #fff;
            color: light-dark(#214392, var(--vb-chrome));
        }

        .app-topbar-logout:hover::before,
        .app-topbar-logout:focus-visible::before {
            transform: translateX(0);
        }

        .app-topbar-logout:active {
            transform: scale(0.97);
        }

        .app-topbar-logout-arrow {
            transition: transform 260ms cubic-bezier(0.4, 0, 0.2, 1);
        }

        .app-topbar-logout:hover .app-topbar-logout-arrow,
        .app-topbar-logout:focus-visible .app-topbar-logout-arrow {
            transform: translateX(3px);
        }

        @media (prefers-reduced-motion: reduce) {
            .app-topbar-logout,
            .app-topbar-logout::before,
            .app-topbar-logout-arrow {
                transition-duration: 1ms;
            }

            .app-topbar-logout:active {
                transform: none;
            }
        }

        .app-topbar-logout:focus-visible {
            outline: 2px solid #fff;
            outline-offset: 2px;
        }

        .app-topbar-logout svg {
            height: 1rem;
            width: 1rem;
            flex-shrink: 0;
        }

        /* Light keeps the brand-blue panel and tints everything in white; dark uses
           the 1A chrome tone with the grey ramp. Same layout either way. */
        .app-sidenav {
            background-color: light-dark(#214392, var(--vb-chrome));
            border-right: 1px solid light-dark(transparent, var(--vb-line));
        }

        .nav-pin { color: light-dark(rgb(255 255 255 / 0.8), var(--vb-ink-muted)); }

        .nav-pin:hover,
        .nav-pin.is-pinned {
            background-color: light-dark(rgb(255 255 255 / 0.12), var(--vb-surface-high));
            color: light-dark(#fff, var(--vb-ink));
        }

        .nav-group {
            border-radius: 0.875rem;
            overflow: hidden;
            background-color: light-dark(rgb(255 255 255 / 0.07), var(--vb-surface));
        }

        .nav-section {
            color: light-dark(rgb(255 255 255 / 0.68), var(--vb-ink-muted));
            transition: color 150ms ease;
        }

        .nav-section:hover { color: light-dark(#fff, var(--vb-ink)); }

        /* The section header is the collapse toggle, but the caret is noise at rest —
           it shows on hover, and stays put whenever a group is actually collapsed. */
        .nav-section-caret { opacity: 0; transition: opacity 150ms ease; }

        .nav-section:hover .nav-section-caret,
        .nav-section:focus-visible .nav-section-caret,
        .nav-section-caret.is-collapsed { opacity: 1; }

        .nav-link {
            color: light-dark(rgb(255 255 255 / 0.86), var(--vb-ink-muted));
            transition: background-color 150ms ease, color 150ms ease;
        }

        .nav-link:hover {
            background-color: light-dark(rgb(255 255 255 / 0.1), var(--vb-surface-hover));
            color: light-dark(#fff, var(--vb-ink));
        }

        .nav-link.is-active {
            background-color: light-dark(rgb(255 255 255 / 0.22), var(--vb-surface-high));
            color: #fff;
            font-weight: 600;
        }

        .nav-badge {
            display: inline-flex;
            min-width: 1.25rem;
            align-items: center;
            justify-content: center;
            padding: 0 0.375rem;
            border-radius: 9999px;
            background-color: light-dark(#fff, var(--vb-brand-fill));
            color: light-dark(#214392, #fff);
            font-size: 0.6875rem;
            font-weight: 600;
            line-height: 1.25rem;
        }

        .pin-toggle rect,
        .pin-toggle path,
        .pin-toggle circle {
            transform-box: fill-box;
            transition: transform 250ms ease-in-out, opacity 250ms ease-in-out,
                rx 250ms ease-in-out, ry 250ms ease-in-out, r 250ms ease-in-out 80ms;
        }
        .pin-toggle .bar-top,
        .pin-toggle .bar-bot { transform-origin: center; }
        .pin-toggle .bar-mid { transform-origin: center top; }

        .pin-toggle .shackle {
            stroke-dasharray: 18;
            stroke-dashoffset: 18;
            transition: stroke-dashoffset 250ms ease-in-out 80ms, transform 250ms ease-in-out;
        }

        .pin-toggle svg:not(.is-locked) .shackle { transform: translateX(-6px); }
        #sidenav:hover .bar-top,
        .nav-pinned .bar-top { transform: scaleX(0.45); opacity: 0; }

        #sidenav:hover .bar-bot,
        .nav-pinned .bar-bot { opacity: 0; }

        #sidenav:hover .bar-mid,
        .nav-pinned .bar-mid { transform: scale(0.778, 5); rx: 3.21px; ry: 0.5px; }

        #sidenav:hover .shackle,
        .nav-pinned .shackle { stroke-dashoffset: 0; }

        #sidenav:hover .keyhole,
        .nav-pinned .keyhole { r: 1.7px; }

        @media (prefers-reduced-motion: reduce) {
            .pin-toggle rect,
            .pin-toggle path,
            .pin-toggle circle { transition: none; }
        }
    `],
    standalone: true,
    imports: [
        RouterOutlet,
        RouterLink,
        RouterLinkActive,
        NgClass,
        MatTooltipModule,
        MatIcon,
    ],
})
export class NavigationPage implements OnInit, OnDestroy {

    private readonly _kngNavBadgeClass = "nav-badge"

    navGroupOpen: Record<string, boolean> = {
        BENEFITI: true,
        KNG: true,
    }

    navPinned = false
    darkMode = document.documentElement.classList.contains("dark")

    user: string = ""
    private _kngSubscription: Subscription | undefined = undefined

    items: NavItem[] = [
        {
            label: "BENEFITI",
            items: [
                { label: "Ankete", routerLink: "/surveys" },
                { label: "Benefiti", routerLink: "/benefits" },
                { label: "Kategorije", routerLink: "/categories" },
                { label: "Lokacije", routerLink: "/locations" },
                { label: "Nagrade", routerLink: "/awards" },
                { label: "Novosti", routerLink: "/news" },
                { label: "Pogodnosti", routerLink: "/perks" },
                { label: "Promotivni sadržaj", routerLink: "/promotional-content" }
            ]
        },
        {
            label: "KNG",
            items: [
                { label: "Sve prijave", routerLink: "/kng" },
                { label: "Prijave u obradi", routerLink: "/kng-in-progress" },
                { label: "Odobrene subvencije", routerLink: "/kng-approved" },
                { label: "Subvencije u tijeku", routerLink: "/kng-contract-generate" },
                { label: "Prosječni mjesečni prihod", routerLink: "/average-monthly-income" },
                { label: "Bodovi radnih mjesta", routerLink: "/kng-position-scores" }
            ]
        },
        {
            label: "VDK",
            routerLink: "/vdk"
        },
    ];

    constructor(
        private _ms: MsalService,
        private _http: HttpClient,
        private _contextService: ContextService,
        private _router: Router,
    ) { }

    get activeCrumb(): { section: string; page: string } {
        const url = this._router.url.split("?")[0]
        for (const section of this.items) {
            const match = (section.items ?? [section]).find(item => {
                const link = item.routerLink as string | undefined
                return !!link && (url === link || url.startsWith(link + "/"))
            })
            if (match) {
                // sections without children (VDK) are their own page, no parent crumb
                return match === section
                    ? { section: "", page: section.label ?? "" }
                    : { section: section.label ?? "", page: match.label ?? "" }
            }
        }
        return { section: "", page: "" }
    }

    ngOnInit(): void {
        this._http.get(environment.graphEndpoint).subscribe(profile => {
            this.user =
                (profile as { userPrincipalName?: string }).userPrincipalName || ""
        })

        this._kngSubscription = this._contextService.getKngSubject()?.subscribe(apps => {
            this.applyKngMenuBadges(apps)
        })
    }

    ngOnDestroy(): void {
        this._kngSubscription?.unsubscribe()
    }

    // The tooltip is the only label this button has, so it stays up across the
    // toggle and re-renders with the next action instead of closing on click.
    togglePin(tooltip: MatTooltip): void {
        this.navPinned = !this.navPinned
        tooltip.show(0)
    }

    toggleDarkMode(): void {
        this.darkMode = !this.darkMode
        document.documentElement.classList.toggle("dark", this.darkMode)
        localStorage.setItem("valben-theme", this.darkMode ? "dark" : "light")
    }

    toggleNavGroup(label: string): void {
        this.navGroupOpen[label] = !this.navGroupOpen[label]
    }

    private applyKngMenuBadges(apps: Application[]): void {
        const kngGroup = this.items.find(i => i.label === "KNG")
        if (!kngGroup?.items) {
            return
        }

        const countStates = (states: ApplicationState[]) =>
            apps.filter(a => states.includes(a.state)).length

        const applyBadge = (routerLink: string, n: number) => {
            const item = kngGroup.items!.find(sub => sub.routerLink === routerLink)
            if (!item) {
                return
            }
            if (n > 0) {
                item.badge = String(n)
                item.badgeStyleClass = this._kngNavBadgeClass
            } else {
                delete item.badge
                delete item.badgeStyleClass
            }
        }

        const kngAllItem = kngGroup.items!.find(sub => sub.routerLink === "/kng")
        if (kngAllItem) {
            delete kngAllItem.badge
            delete kngAllItem.badgeStyleClass
        }

        applyBadge("/kng-in-progress", countStates([
            ApplicationState.Submitted,
            ApplicationState.WaitingCommission,
        ]))

        const readyForContractGeneration = apps.filter(
            a =>
                a.state === ApplicationState.Approved &&
                a.landlordData?.submitted === true,
        ).length
        const readyForSignedContractsUpload = apps.filter(
            a => a.state === ApplicationState.ContractGenerated,
        ).length

        applyBadge("/kng-approved", readyForContractGeneration + readyForSignedContractsUpload)

        applyBadge("/kng-contract-generate", countStates([ApplicationState.SignedContractsUploaded]))

        this.items = [...this.items]
    }

    logout() {
        this._ms.logoutRedirect({
            postLogoutRedirectUri: environment.appBaseUrl,
        })
    }
}
